import React, { type FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import type { WithRouterProps } from 'next/dist/client/with-router';
import Link from 'next/link';
import { withRouter } from 'next/router';
import { Typography, Grid, Button } from '@mui/material';
import { Waypoint } from 'react-waypoint';
import { getUserDetails, getEntitlements, trackNavigatedToPage } from '@surfline/web-common';

import ContentContainer from 'components/ContentContainer';
import { PostsBlock, LoadMorePosts } from 'components/Editorial';
import EditorialFeatureCard from 'components/EditorialFeatureCard';
import FeedCard from 'components/FeedCard';
import GoogleDFP from 'components/GoogleDFP';
import HeaderAd from 'components/HeaderAd';
import { Chevron } from 'components/Icons';
import MetaTags from 'components/MetaTags';
import PageLoading from 'components/PageLoading';
import config from 'config';
import getTaxonomyDetails from 'selectors/editorialTaxonomyDetails';
import getTaxonomyPosts from 'selectors/editorialTaxonomyPosts';
import { AppState } from 'stores/index';
import type { BreadCrumbs } from 'types/breadCrumbs';
import type { UserEntitlements, UserDetails } from 'types/user';
import loadAdConfig from 'utils/adConfig';
import { getUserIsEntitled, useUserPermissionStatus } from 'selectors/user';
import { getIsPremiumWithAdsUser } from 'utils/user';
import {
  loadMoreEditorialTaxonomyDetails,
  startInfiniteScrollingDetails,
} from 'actions/editorialTaxonomyDetails';

import styles from './EditorialTaxonomy.module.scss';

const CATEGORY_WHITELIST = [
  'ALL',
  'PREMIUM',
  'BREAKING',
  'FEATURES',
  'VIDEO',
  'TRAVEL',
  'GEAR',
  'TRAINING',
  'SCIENCE',
];

const getRecentPostsClassName = (hasSubcategories: any, isEntitled: any) =>
  classnames({
    'sl-taxonomy-term-recent-posts': true,
    'sl-taxonomy-term-recent-posts--with-categories': hasSubcategories,
    'sl-taxonomy-term-recent-posts--not-premium': !isEntitled,
  });

const getNavClassName = (isActive: boolean) =>
  classnames({
    'sl-editorial-taxonomy__header__nav__terms__term': true,
    'sl-editorial-taxonomy__header__nav__terms__term--active': isActive,
  });

const getArticleImage = (media: any, imageResizing: any, feature = false) => {
  const mediaUrl = feature && media?.promobox1x ? media?.promobox1x : media?.feed2x;
  const settings = feature ? 'w=1200,q=85,f=auto,fit=contain' : 'w=740,q=75,f=auto,fit=contain';
  return imageResizing ? `${config.cloudflareImageResizingUrl(mediaUrl, settings)}` : mediaUrl;
};

interface TaxonomyDetails {
  name: string;
  slug: string;
  taxonomy: string;
  id: number;
  subCategories: Array<any>;
  navTerms: Array<any>;
  breadcrumbs: BreadCrumbs;
  hasMore?: boolean;
  total?: number;
  offset?: number;
  limit?: number;
}

interface TaxonomyPosts {
  posts: Array<any>;
  loading: boolean;
  infiniteScroll: boolean;
}

interface Props extends WithRouterProps {
  hideAllSubCategories?: boolean;
  taxonomyDetails: TaxonomyDetails | null;
  taxonomyPosts: TaxonomyPosts | null;
  isEntitled: boolean;
  entitlements: UserEntitlements;
  userDetails: Partial<UserDetails> | null;
  infiniteScrollDetails: boolean;
  doLoadMoreDetails: () => void;
  doStartInfiniteScrollingDetails: () => void;
}

const EditoralTaxonomy: FunctionComponent<Props> = ({
  entitlements,
  isEntitled,
  router: {
    asPath,
    query: { subCategory, term },
  },
  taxonomyDetails,
  taxonomyPosts,
  userDetails,
  hideAllSubCategories = false,
  infiniteScrollDetails,
  doLoadMoreDetails,
  doStartInfiniteScrollingDetails,
}) => {
  const { hasAdFreePermissions } = useUserPermissionStatus();
  const isPremiumWithAdsUser = getIsPremiumWithAdsUser(entitlements);

  // Memoize derived values to prevent unnecessary re-computations
  const taxonomy = useMemo(() => taxonomyDetails?.taxonomy, [taxonomyDetails?.taxonomy]);
  const taxonomyId = useMemo(() => taxonomyDetails?.id, [taxonomyDetails?.id]);
  const currentSlug = useMemo(() => taxonomyDetails?.slug, [taxonomyDetails?.slug]);

  const subCategories = taxonomyDetails && taxonomyDetails.subCategories;

  const navTerms = taxonomyDetails && taxonomyDetails.navTerms;

  const isAllNews = taxonomyDetails?.slug === 'category';

  // Index pages: the API returns slug === taxonomy (e.g. slug="tag", taxonomy="tag")
  // Term pages return a specific slug (e.g. slug="big-wave", taxonomy="tag")
  const isIndexPage = useMemo(
    () => taxonomyDetails?.slug === taxonomyDetails?.taxonomy,
    [taxonomyDetails?.slug, taxonomyDetails?.taxonomy],
  );

  const breadcrumbs = taxonomyDetails && taxonomyDetails.breadcrumbs;

  const hasSubcategories = subCategories && subCategories.length > 0;

  // Memoize adTarget array creation
  const adTarget = useMemo(() => {
    const targets: any = [];
    if (taxonomy) {
      if (taxonomy.indexOf('series') !== -1) {
        targets.push(['contentSeriesId', taxonomyId]);
      } else if (taxonomy.indexOf('category') !== -1) {
        targets.push(['contentCategoryId', taxonomyId]);
      } else if (taxonomy.indexOf('tag') !== -1) {
        targets.push(['contentTagId', taxonomyId]);
      }
      targets.push(['qaFlag', false]);
    }
    return targets;
  }, [taxonomy, taxonomyId]);

  // Memoize filtered and mapped navigation terms
  const filteredNavTerms = useMemo(
    () =>
      navTerms
        ?.filter((navTerm: any) => CATEGORY_WHITELIST.indexOf(navTerm.slug.toUpperCase()) !== -1)
        .map((navTerm: any) => (
          <Link
            href={`/category/${navTerm.slug}`}
            key={navTerm.slug}
            className={getNavClassName(currentSlug === navTerm.slug)}
          >
            {navTerm.name}
          </Link>
        )),
    [navTerms, currentSlug],
  );

  // Memoize filtered and mapped subcategories
  const filteredSubCategories = useMemo(
    () =>
      subCategories
        ?.filter((cat: any) => {
          const { posts } = cat;
          return posts && !(cat.breadcrumbs && isAllNews);
        })
        .map((cat: any) => {
          const { posts, name, slug } = cat;
          return (
            <PostsBlock
              key={slug}
              name={name}
              posts={posts}
              slug={slug}
              taxonomy={taxonomy || ''}
              imageResizing
            />
          );
        }),
    [subCategories, isAllNews, taxonomy],
  );

  const trackPage = useCallback(
    ({ taxonomy: taxonomyValue, name, pageId }: any) => {
      const pageProperties: any = {
        channel: 'editorial',
        category: 'editorial',
        path: asPath,
        url: window && window.location.href,
      };

      const pageName = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;

      if (!taxonomyValue || taxonomyValue === 'category') {
        pageProperties.categoryName = pageName || 'Category';
        pageProperties.categoryId = pageId || 0;
        pageProperties.name = pageProperties.categoryName;
      } else if (taxonomyValue === 'series') {
        pageProperties.seriesName = pageName || 'Series';
        pageProperties.seriesId = pageId || 0;
        pageProperties.name = pageProperties.seriesName;
      } else if (taxonomyValue === 'tag') {
        pageProperties.tagName = pageName || 'Tag';
        pageProperties.tagId = pageId || 0;
        pageProperties.name = pageProperties.tagName;
      }

      trackNavigatedToPage(pageName, pageProperties);
    },
    [asPath],
  );

  useEffect(() => {
    if (taxonomyDetails) {
      trackPage({
        taxonomy,
        name: taxonomyDetails.name,
        pageId: taxonomyDetails.id,
      });
    }
  }, [trackPage, taxonomy, taxonomyDetails]);

  // Calculate adViewType before using it in memoized functions
  const adViewType = useMemo(() => {
    if (taxonomy) {
      if (taxonomy.indexOf('series') !== -1) {
        return 'CONTENT_SERIES';
      }
      if (taxonomy.indexOf('category') !== -1) {
        return 'CONTENT_CATEGORY';
      }
      if (taxonomy.indexOf('tag') !== -1) {
        return 'CONTENT_TAG';
      }
    }
    return 'CONTENT_HOME';
  }, [taxonomy]);

  // Memoize filtered and mapped posts
  const filteredPosts = useMemo(
    () =>
      taxonomyPosts?.posts
        ?.filter((post: any) => post && post.permalink)
        .map((post: any, index: number) => {
          const timestamp = Math.floor(new Date(post.createdAt).getTime() / 1000);
          const uniqueKey = `${post.permalink}-${index}`;

          return post.isFeatured ? (
            <Grid item mobile={12} key={uniqueKey}>
              <section>
                {!isEntitled || isPremiumWithAdsUser ? (
                  <div className="sl-taxonomy-term-recent-posts__ad">
                    <GoogleDFP
                      adConfig={loadAdConfig(
                        'editorialTaxonomyMidPage',
                        adTarget,
                        entitlements,
                        !!userDetails,
                        adViewType as any,
                      )}
                    />
                  </div>
                ) : null}
                {post.id ? (
                  <EditorialFeatureCard
                    isPremium={post.premium}
                    imageUrl={getArticleImage(post.media, true, true)}
                    permalink={post.permalink}
                    title={post.title}
                    onClickLink={() => null}
                  />
                ) : null}
              </section>
            </Grid>
          ) : (
            <Grid item mobile={12} tablet={6} desktop={4} key={uniqueKey}>
              <FeedCard
                category={post.categories[0]}
                createdAt={timestamp}
                imageUrl={getArticleImage(post.media, true)}
                isPremium={post.premium}
                onClickLink={() => null}
                onClickTag={() => null}
                permalink={post.permalink}
                showTimestamp={!Number.isNaN(Number(timestamp))}
                subtitle={post.subtitle}
                title={post.title}
              />
            </Grid>
          );
        }),
    [
      taxonomyPosts?.posts,
      isEntitled,
      isPremiumWithAdsUser,
      adTarget,
      entitlements,
      userDetails,
      adViewType,
      taxonomy,
      taxonomyId,
    ],
  );

  // Set up data for the HeaderAd component - moved before early return
  const { adConfigName, adTargets } = useMemo(() => {
    let configName = '';
    const targets = [];

    if (taxonomy?.indexOf('series') !== -1) {
      targets.push(['contentSeriesId', 1]);
      configName = 'editorial_series';
    } else if (taxonomy?.indexOf('category') !== -1) {
      targets.push(['contentCategoryId', 1]);
      configName = 'editorial_category';
    } else if (taxonomy?.indexOf('tag') !== -1) {
      targets.push(['contentTagId', 1]);
      configName = 'editorial_tag';
    }
    targets.push(['qaFlag', false]);

    return { adConfigName: configName, adTargets: targets };
  }, [taxonomy]);

  if (!taxonomyDetails || (!isIndexPage && !taxonomyPosts)) return null;

  let pageTitle = taxonomy === 'category' ? null : taxonomy;

  if (term || subCategory) pageTitle = term?.toString() || subCategory?.toString() || '';

  pageTitle = pageTitle ? ` ${pageTitle.charAt(0).toUpperCase()}${pageTitle.slice(1)} ` : ' ';

  const headerAdStyles = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  };

  return (
    <>
      <HeaderAd
        adConfigName={adConfigName}
        adTarget={adTargets}
        entitlements={entitlements}
        isVisible={!hasAdFreePermissions}
        styles={headerAdStyles}
        userDetails={userDetails}
      />
      <div className={styles.editorialTaxonomy} data-testid="sl-editorial-taxonomy-wrapper">
        <div className="sl-editorial-taxonomy">
          <MetaTags
            title={`Surfing${pageTitle}Articles:
            Latest Surf${pageTitle}News, Videos, & Photos at Surfline`}
            description={`Access Surfline's daily digest of the latest in surf
            ${pageTitle.toLowerCase()}journalism. Get breaking news in all things surf,
            featured stories, and our renowned surf forecast and science articles.`}
            permalink={undefined /* TODO: Add taxonomy.permalink from response */}
            urlPath=""
          />
          <ContentContainer>
            <div className="sl-editorial-taxonomy__header">
              {breadcrumbs && (
                <div className="sl-editorial-taxonomy__header__breadcrumbs">
                  <div className="sl-editorial-taxonomy__header__breadcrumbs__crumb">
                    <Link href="/surf-news/">All Surf News</Link>
                    <Chevron direction="right" />
                  </div>
                  {breadcrumbs?.map((crumb: any) => (
                    <div
                      className="sl-editorial-taxonomy__header__breadcrumbs__crumb"
                      key={`${crumb.slug}-${crumb.name}`}
                    >
                      <Link href={`/${taxonomy || 'category'}/${crumb.slug}`}>{crumb.name}</Link>
                      <Chevron direction="right" />
                    </div>
                  ))}
                </div>
              )}
              <Typography variant="title1" data-testid="taxonomy-title" component="h1">
                {isAllNews ? 'Surf News' : taxonomyDetails.name}
                {taxonomyDetails.name === 'Surfology' && <sup>&reg;</sup>}
              </Typography>
              {!breadcrumbs && (
                <div className="sl-editorial-taxonomy__header__nav">
                  <div className="sl-editorial-taxonomy__header__nav__terms">
                    <Link href="/surf-news/" className={getNavClassName(isAllNews)}>
                      All
                    </Link>
                    {filteredNavTerms}
                  </div>
                </div>
              )}
            </div>
            {isIndexPage ? (
              // Index page: show paginated taxonomy terms with Load More
              <>
                {!hideAllSubCategories && (
                  <div className="sl-tax-cat-blocks" data-testid="sl-tax-cat-blocks">
                    {filteredSubCategories}
                  </div>
                )}
                {!hideAllSubCategories && taxonomyDetails.hasMore && (
                  <div className="sl-taxonomy-load-more" data-testid="load-more-taxonomy">
                    {infiniteScrollDetails ? (
                      <Waypoint onEnter={doLoadMoreDetails} />
                    ) : (
                      <Button
                        className="sl-taxonomy-load-more__button"
                        variant="secondary"
                        onClick={doStartInfiniteScrollingDetails}
                      >
                        Load More
                      </Button>
                    )}
                  </div>
                )}
                {hideAllSubCategories && (
                  <>
                    {taxonomyPosts?.loading && !taxonomyPosts?.infiniteScroll ? (
                      <PageLoading />
                    ) : (
                      <div className={getRecentPostsClassName(hasSubcategories, isEntitled)}>
                        <h2>Recent</h2>
                        <Grid
                          container
                          spacing={4}
                          className="sl-taxonomy-term-recent-posts__posts"
                        >
                          {filteredPosts}
                        </Grid>
                        <LoadMorePosts
                          subCategory={subCategory?.toString() || ''}
                          taxonomy={taxonomy || ''}
                          term={term?.toString() || ''}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              // Term page: show subcategories + recent posts with existing Load More
              <>
                {taxonomyPosts?.loading && !taxonomyPosts?.infiniteScroll ? (
                  <PageLoading />
                ) : (
                  <>
                    {!hideAllSubCategories && (
                      <div className="sl-tax-cat-blocks" data-testid="sl-tax-cat-blocks">
                        {filteredSubCategories}
                      </div>
                    )}
                    <div className={getRecentPostsClassName(hasSubcategories, isEntitled)}>
                      <h2>Recent</h2>
                      <Grid container spacing={4} className="sl-taxonomy-term-recent-posts__posts">
                        {filteredPosts}
                      </Grid>
                      <LoadMorePosts
                        subCategory={subCategory?.toString() || ''}
                        taxonomy={taxonomy || ''}
                        term={term?.toString() || ''}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </ContentContainer>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: AppState) => ({
    taxonomyDetails: getTaxonomyDetails(state),
    taxonomyPosts: getTaxonomyPosts(state),
    isEntitled: getUserIsEntitled(state),
    userDetails: getUserDetails(state),
    entitlements: getEntitlements(state),
    infiniteScrollDetails: (state as any).editorialTaxonomyDetails?.infiniteScroll ?? false,
  }),
  (dispatch: any) => ({
    doLoadMoreDetails: () => dispatch(loadMoreEditorialTaxonomyDetails()),
    doStartInfiniteScrollingDetails: () => dispatch(startInfiniteScrollingDetails()),
  }),
)(withRouter(EditoralTaxonomy));
